import {
  deleteHomeRoomIdbKey,
  loadHomeRoomFromIdb,
  loadIdbValue,
  saveHomeRoomToIdb,
  setIdbValue,
} from "@/home/home-idb";
import {
  createDefaultHomeRoom,
  homeRoomFromStored,
  normalizeHomeRoom,
  type HomeRoomData,
  loadHomeRoom,
  saveHomeRoom,
} from "@/home/storage";
import { isStorageQuotaError } from "@/home/home-idb";

/** Default scene id for player home (`/home`). Uses legacy IDB key = userId. */
export const HOME_SCENE_ID = "home";

export type CreatorSceneMeta = {
  id: string;
  name: string;
  updatedAt: number;
};

type SceneIndex = {
  scenes: CreatorSceneMeta[];
};

const SCENE_INDEX_PREFIX = "scene-index:";
const SCENE_ROOM_PREFIX = "scene-room:";

function sceneIndexKey(userId: string): string {
  return `${SCENE_INDEX_PREFIX}${userId}`;
}

function sceneRoomKey(userId: string, sceneId: string): string {
  return `${SCENE_ROOM_PREFIX}${userId}:${sceneId}`;
}

function isHomeScene(sceneId: string): boolean {
  return sceneId === HOME_SCENE_ID;
}

export function newCreatorSceneId(): string {
  return `sc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

async function loadSceneIndex(userId: string): Promise<SceneIndex> {
  const raw = await loadIdbValue<SceneIndex>(sceneIndexKey(userId));
  if (!raw || typeof raw !== "object") return { scenes: [] };
  const scenes = raw.scenes;
  if (!Array.isArray(scenes)) return { scenes: [] };
  return {
    scenes: scenes.filter(
      (s) =>
        s &&
        typeof s === "object" &&
        typeof (s as CreatorSceneMeta).id === "string" &&
        typeof (s as CreatorSceneMeta).name === "string",
    ) as CreatorSceneMeta[],
  };
}

async function saveSceneIndex(userId: string, index: SceneIndex): Promise<void> {
  await setIdbValue(sceneIndexKey(userId), index);
}

export async function listCreatorScenes(userId: string): Promise<CreatorSceneMeta[]> {
  const index = await loadSceneIndex(userId);
  return [...index.scenes].sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function createCreatorScene(
  userId: string,
  name: string,
): Promise<CreatorSceneMeta> {
  const trimmed = name.trim().slice(0, 48);
  if (!trimmed) {
    throw new Error("ใส่ชื่อฉาก");
  }
  const meta: CreatorSceneMeta = {
    id: newCreatorSceneId(),
    name: trimmed,
    updatedAt: Date.now(),
  };
  const index = await loadSceneIndex(userId);
  index.scenes.unshift(meta);
  await saveSceneIndex(userId, index);
  await saveSceneRoom(userId, meta.id, createDefaultHomeRoom());
  return meta;
}

export async function renameCreatorScene(
  userId: string,
  sceneId: string,
  name: string,
): Promise<void> {
  if (isHomeScene(sceneId)) return;
  const trimmed = name.trim().slice(0, 48);
  if (!trimmed) throw new Error("ใส่ชื่อฉาก");
  const index = await loadSceneIndex(userId);
  const row = index.scenes.find((s) => s.id === sceneId);
  if (!row) throw new Error("ไม่พบฉาก");
  row.name = trimmed;
  row.updatedAt = Date.now();
  await saveSceneIndex(userId, index);
}

export async function touchCreatorScene(userId: string, sceneId: string): Promise<void> {
  if (isHomeScene(sceneId)) return;
  const index = await loadSceneIndex(userId);
  const row = index.scenes.find((s) => s.id === sceneId);
  if (!row) return;
  row.updatedAt = Date.now();
  await saveSceneIndex(userId, index);
}

export async function deleteCreatorScene(userId: string, sceneId: string): Promise<void> {
  if (isHomeScene(sceneId)) {
    throw new Error("ลบบ้านหลักไม่ได้");
  }
  const index = await loadSceneIndex(userId);
  index.scenes = index.scenes.filter((s) => s.id !== sceneId);
  await saveSceneIndex(userId, index);
  await deleteHomeRoomIdbKey(sceneRoomKey(userId, sceneId));
}

export async function loadSceneRoom(userId: string, sceneId: string): Promise<HomeRoomData> {
  if (isHomeScene(sceneId)) {
    return loadHomeRoom(userId);
  }
  const fromIdb = await loadHomeRoomFromIdb(sceneRoomKey(userId, sceneId));
  return homeRoomFromStored(fromIdb) ?? createDefaultHomeRoom();
}

export async function saveSceneRoom(
  userId: string,
  sceneId: string,
  data: HomeRoomData,
): Promise<void> {
  if (isHomeScene(sceneId)) {
    await saveHomeRoom(userId, data);
    return;
  }
  try {
    await saveHomeRoomToIdb(
      sceneRoomKey(userId, sceneId),
      normalizeHomeRoom(data),
    );
    await touchCreatorScene(userId, sceneId);
  } catch (err) {
    if (isStorageQuotaError(err)) {
      throw new Error("พื้นที่เก็บข้อมูลเต็ม — ลบชิ้นที่ไม่ใช้ หรือสร้างทีละน้อย");
    }
    throw err;
  }
}

export async function getCreatorSceneMeta(
  userId: string,
  sceneId: string,
): Promise<CreatorSceneMeta | null> {
  if (isHomeScene(sceneId)) {
    return { id: HOME_SCENE_ID, name: "บ้านของฉัน", updatedAt: Date.now() };
  }
  const index = await loadSceneIndex(userId);
  return index.scenes.find((s) => s.id === sceneId) ?? null;
}
